import {
  createRoute,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router'
import { useState } from 'react'
import { Save } from 'lucide-react'
import rootRoute from './rootRoute'
import { Button, errorToast, toast } from '@/components/ui'
import AuthLayout from '@/components/layout/AuthLayout'
import PasswordField from '@/components/PasswordField'
import { $client } from '@/lib/fetch'
import { validatePassword } from '@/lib/auth'

const ResetPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const search = useSearch({ from: '/reset-password' })
  const router = useRouter()
  const navigate = useNavigate()

  const invalid = !search.email || !search.token

  const reset = async () => {
    if (invalid) return
    if (password !== confirm) {
      toast({
        color: 'danger',
        title: 'Les mots de passe ne correspondent pas',
      })
      return
    }
    const err = validatePassword(password)
    if (err) {
      toast({ color: 'danger', title: err })
      return
    }

    setIsLoading(true)
    const resp = await $client
      .POST('/api/auth/forgot-password-change', {
        body: {
          email: search.email!,
          token: search.token!,
          new_password: password,
        },
      })
      .finally(() => setIsLoading(false))

    if (resp.error != null) {
      errorToast('Échec de la réinitialisation', resp.error)
    } else {
      toast({ color: 'success', title: 'Mot de passe modifié avec succès !' })
      setTimeout(
        () => router.navigate({ to: '/login', search: { go: undefined } }),
        2000,
      )
    }
  }

  return (
    <AuthLayout
      panelTitle="Nouveau mot de passe"
      panelSub="Choisissez un mot de passe sûr pour protéger votre compte."
    >
      <h1 style={{ fontSize: 'var(--text-h2)', margin: '0 0 8px' }}>
        Définir un mot de passe
      </h1>
      {invalid ? (
        <>
          <p
            style={{ fontSize: 15.5, color: 'var(--danger)', marginBottom: 24 }}
          >
            Le lien de réinitialisation est invalide ou a expiré.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate({ to: '/forgot-password' })}
          >
            Demander un nouveau lien
          </Button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            reset()
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
        >
          <PasswordField
            label="Nouveau mot de passe"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <PasswordField
            label="Confirmer le mot de passe"
            placeholder="••••••••"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            type="submit"
            loading={isLoading}
            iconLeft={<Save size={18} />}
          >
            Enregistrer
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search) => ({
    email: search.email as string | undefined,
    token: search.token as string | undefined,
  }),
}).lazy(() => import('./ResetPasswordPage.lazy').then((m) => m.Route))

export default ResetPasswordPage
